import React, { useCallback } from 'react';
import { FieldError, InputContext, Input as RacInput, InputProps as RacInputProps, Text, TextField, useContextProps } from 'react-aria-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';

import { InputLabel } from '../../../elements/input-label';
import { TextBlock } from '../../../elements/text-block';

import './index.scss';

type InputProps = {
  leading?: React.ReactNode;
  iconLeading?: IconDefinition;
  iconTrailing?: IconDefinition;
} & RacInputProps;

const Input = React.forwardRef((props: InputProps, ref: React.Ref<HTMLInputElement>) => {
  const { leading, iconLeading, iconTrailing, ...restProps } = props;
  const [inputProps, inputRef] = useContextProps(restProps, ref, InputContext);

  // Focus input when clicking on the wrapper
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      inputRef.current?.focus();
    },
    [inputRef],
  );

  return (
    <div className="rac-input-field-input-wrapper" onClick={handleClick}>
      {leading && leading}
      {iconLeading && <FontAwesomeIcon icon={iconLeading} className={`myd-input--leading-icon`} aria-hidden />}
      <div className="rac-input-wrapper">
        <TextBlock variant="L-regular-1L">
          <RacInput {...inputProps} ref={inputRef} />
        </TextBlock>
      </div>
      {iconTrailing && <FontAwesomeIcon icon={iconTrailing} className={`myd-input--trailing-icon`} aria-hidden />}
    </div>
  );
});

type InputFieldProps = {
  label: string;
  description?: string;
  errorMessage?: string;
  tipTitle?: string;
  tipDescription?: string;
  isReadOnly?: boolean;
} & InputProps;

export const InputField = (props: InputFieldProps) => {
  const { label, description, errorMessage, isReadOnly, tipTitle, tipDescription, ...inputProps } = props;

  return (
    <TextField isInvalid={Boolean(errorMessage)} isReadOnly={isReadOnly}>
      <InputLabel asterisk={props.required} description={description} tipTitle={tipTitle} tipDescription={tipDescription}>
        {label}
      </InputLabel>
      <Input type="text" required {...inputProps} />
      {description && (
        <Text slot="description">
          <TextBlock variant="XXS-regular-hint-text">{description}</TextBlock>
        </Text>
      )}
      <FieldError>
        <TextBlock variant="XXS-regular-error-message">{errorMessage}</TextBlock>
      </FieldError>
    </TextField>
  );
};

export default InputField;
